import { useEffect, useMemo, useRef, useState } from 'react';
import { TOOLS } from '@/registry/tools';
import { selectActiveToolId, usePrefs } from '@/store/prefs';

type Mode = 'browse' | 'tool';

export type BrowserSection = 'results' | 'recent' | 'favorites' | 'all';

/**
 * A single visual position in the tool browser. The same tool can appear in
 * multiple sections (Recent, Favorites, All), so we track positions by
 * (section, toolId) rather than tool id alone — that's what makes the
 * highlight only fall on the *one* row the user is actually pointing at,
 * not all three copies.
 */
export interface BrowserItem {
  section: BrowserSection;
  toolId: string;
}

interface NavReturn {
  mode: Mode;
  activeToolId: string | null;
  query: string;
  setQuery: (q: string) => void;
  /** Items as they appear visually, top-to-bottom. Index in here is what
   *  highlightIndex points at. */
  visualItems: BrowserItem[];
  highlightIndex: number;
  setHighlightIndex: (i: number) => void;
  /** True iff the highlight currently lands on (section, toolId). */
  isHighlighted: (section: BrowserSection, toolId: string) => boolean;
  openTool: (id: string) => void;
  back: () => void;
  searchInputRef: React.RefObject<HTMLInputElement | null>;
}

const MAX_VISIBLE_RECENTS = 6;

export function useToolNavigation(): NavReturn {
  const activeToolId = usePrefs(selectActiveToolId);
  const setActiveTab = usePrefs((s) => s.setActiveTab);
  const storeOpenTool = usePrefs((s) => s.openTool);
  const pushRecent = usePrefs((s) => s.pushRecent);
  const favorites = usePrefs((s) => s.favorites);
  const recents = usePrefs((s) => s.recents);
  const tabs = usePrefs((s) => s.tabs);

  const [query, setQueryRaw] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(0);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const mode: Mode = activeToolId ? 'tool' : 'browse';

  /** Typing in search auto-routes the user back to the browse view so the
   *  results render somewhere visible. Otherwise typing while a tool tab is
   *  open does nothing the user can see. */
  const setQuery = (q: string) => {
    setQueryRaw(q);
    if (q && activeToolId) setActiveTab(null);
  };

  const visualItems = useMemo<BrowserItem[]>(() => {
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      const matched = TOOLS.filter((t) => {
        const haystack = [t.name, t.description, t.id, ...t.keywords]
          .join(' ')
          .toLowerCase();
        return haystack.includes(q);
      });
      return matched.map((t) => ({ section: 'results' as const, toolId: t.id }));
    }
    const recentIds = recents.filter((id) => TOOLS.some((t) => t.id === id)).slice(0, MAX_VISIBLE_RECENTS);
    const favIds = favorites.filter((id) => TOOLS.some((t) => t.id === id));
    const items: BrowserItem[] = [];
    for (const id of recentIds) items.push({ section: 'recent', toolId: id });
    for (const id of favIds) items.push({ section: 'favorites', toolId: id });
    for (const t of TOOLS) items.push({ section: 'all', toolId: t.id });
    return items;
  }, [query, favorites, recents]);

  const isHighlighted = (section: BrowserSection, toolId: string): boolean => {
    const it = visualItems[highlightIndex];
    return !!it && it.section === section && it.toolId === toolId;
  };

  const openTool = (id: string) => {
    storeOpenTool(id);
    pushRecent(id);
    setQueryRaw('');
  };

  const back = () => {
    if (query) {
      setQueryRaw('');
      return;
    }
    if (activeToolId) setActiveTab(null);
  };

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toLowerCase().includes('mac');
      const cmd = isMac ? e.metaKey : e.ctrlKey;

      if (cmd && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (activeToolId) setActiveTab(null);
        setTimeout(() => searchInputRef.current?.focus(), 0);
        return;
      }

      if (cmd && /^[1-9]$/.test(e.key)) {
        const idx = Number(e.key) - 1;
        const tab = tabs[idx];
        if (tab) {
          e.preventDefault();
          setActiveTab(tab.id);
        }
        return;
      }

      if (e.key === 'Escape') {
        const target = e.target as HTMLElement | null;
        const tag = target?.tagName?.toLowerCase();
        const isTyping =
          tag === 'input' ||
          tag === 'textarea' ||
          target?.isContentEditable === true;
        if (mode === 'browse') {
          if (query) {
            e.preventDefault();
            setQueryRaw('');
          }
        } else {
          if (isTyping) return;
          e.preventDefault();
          setActiveTab(null);
        }
        return;
      }

      if (mode !== 'browse') return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightIndex(Math.min(visualItems.length - 1, highlightIndex + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightIndex(Math.max(0, highlightIndex - 1));
      } else if (e.key === 'Enter') {
        const item = visualItems[highlightIndex];
        if (item) {
          e.preventDefault();
          openTool(item.toolId);
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, query, activeToolId, highlightIndex, visualItems, tabs]);

  return {
    mode,
    activeToolId,
    query,
    setQuery,
    visualItems,
    highlightIndex,
    setHighlightIndex,
    isHighlighted,
    openTool,
    back,
    searchInputRef,
  };
}
